
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { useIsMobile } from "@/hooks/use-mobile";
import { useTranslation } from "@/hooks/useTranslation";
import { ProfileTab } from "@/components/settings/ProfileTab";
import { SecurityTab } from "@/components/settings/SecurityTab";
import { AppearanceTab } from "@/components/settings/AppearanceTab";
import { FeedbackTab } from "@/components/settings/FeedbackTab";
import { DangerTab } from "@/components/settings/DangerTab";
import { LanguageTab } from "@/components/settings/LanguageTab";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { 
  User, Settings as SettingsIcon, Shield, Paintbrush, 
  Globe, MessageSquare, Database, LogOut, AlertCircle, ChevronLeft
} from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";

export default function Settings() {
  const isMobile = useIsMobile();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    username: "",
    displayName: "",
    email: "",
    phoneNumber: "",
    usernameUpdateAllowed: true,
    daysUntilUsernameChange: 0,
    linkOne: "",
    linkTwo: ""
  });
  const [userLanguage, setUserLanguage] = useState("en");
  const [activeTab, setActiveTab] = useState("profile");

  useEffect(() => {
    getProfile();
    detectUserLanguage();
    
    const handleLanguageChange = (event: CustomEvent) => {
      setUserLanguage(event.detail.language);
      document.documentElement.lang = event.detail.language;
    };
    
    window.addEventListener('languageChange', handleLanguageChange as EventListener);
    
    return () => {
      window.removeEventListener('languageChange', handleLanguageChange as EventListener);
    };
  }, []);

  const getProfile = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      console.log("Fetching user profile for settings page");
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError) {
        console.error("Error fetching auth user:", userError);
        throw userError;
      }
      
      if (!user) {
        console.log("No authenticated user found, redirecting to auth");
        navigate("/auth", { replace: true });
        return;
      }
      
      console.log("User found, fetching profile data");
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();
      
      if (profileError) {
        console.error("Error fetching profile data:", profileError);
        throw profileError;
      }
      
      console.log("Profile data fetched successfully:", profileData);
      
      const username_updated_at = user.user_metadata?.username_updated_at;
      const now = new Date();
      const lastUpdate = username_updated_at ? new Date(username_updated_at) : null;
      const daysSinceUpdate = lastUpdate ? Math.floor((now.getTime() - lastUpdate.getTime()) / (1000 * 60 * 60 * 24)) : 91;
      const daysRemaining = Math.max(90 - daysSinceUpdate, 0);
      
      setFormData({
        username: user.user_metadata?.username || profileData?.username || "",
        displayName: user.user_metadata?.display_name || profileData?.display_name || "",
        email: user.email || "",
        phoneNumber: user.phone || "",
        usernameUpdateAllowed: daysSinceUpdate > 90,
        daysUntilUsernameChange: daysRemaining,
        linkOne: profileData?.link_one || "",
        linkTwo: profileData?.link_two || ""
      });
    } catch (error: any) {
      console.error("Error in getProfile:", error);
      setError(error.message || "Failed to load profile data");
      toast.error("Failed to load profile data");
    } finally {
      setIsLoading(false);
    }
  };

  const detectUserLanguage = async () => {
    try {
      const savedLanguage = localStorage.getItem("appLanguage");
      if (savedLanguage) {
        setUserLanguage(savedLanguage);
        document.documentElement.lang = savedLanguage;
        return;
      }

      const browserLang = navigator.language.split("-")[0];
      const supportedLanguages = ["en", "es", "fr", "de", "zh", "ja", "ko", "pt"];
      
      const detectedLanguage = supportedLanguages.includes(browserLang) ? browserLang : "en";
      
      setUserLanguage(detectedLanguage);
      localStorage.setItem("appLanguage", detectedLanguage);
      document.documentElement.lang = detectedLanguage;
      console.log(`Language set based on browser language: ${detectedLanguage}`);
    } catch (error) {
      console.error("Error detecting language:", error);
      setUserLanguage("en");
      document.documentElement.lang = "en";
    }
  };

  const getTabIcon = (tab: string) => {
    switch (tab) {
      case "profile": return <User className="h-5 w-5" />;
      case "security": return <Shield className="h-5 w-5" />;
      case "appearance": return <Paintbrush className="h-5 w-5" />;
      case "language": return <Globe className="h-5 w-5" />;
      case "feedback": return <MessageSquare className="h-5 w-5" />;
      case "data": return <Database className="h-5 w-5" />;
      default: return <SettingsIcon className="h-5 w-5" />;
    }
  };

  const handleSignOut = async () => {
    try {
      await supabase.auth.signOut();
      toast.success(t('sign_out_success'));
      navigate("/", { replace: true });
    } catch (error: any) {
      console.error("Sign out error:", error);
      toast.error(error.message);
    }
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  const renderActiveTabContent = () => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center p-8">
          <LoadingSpinner size="lg" />
        </div>
      );
    }

    if (error) {
      return (
        <Alert variant="destructive" className="my-4">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      );
    }

    switch (activeTab) {
      case "profile":
        return <ProfileTab formData={formData} setFormData={setFormData} isMobile={isMobile} />;
      case "security":
        return <SecurityTab isMobile={isMobile} />;
      case "appearance":
        return <AppearanceTab isMobile={isMobile} />;
      case "language":
        return <LanguageTab isMobile={isMobile} />;
      case "feedback":
        return <FeedbackTab isMobile={isMobile} />;
      case "data":
        return <DangerTab isMobile={isMobile} />;
      default:
        return <ProfileTab formData={formData} setFormData={setFormData} isMobile={isMobile} />;
    }
  };

  const tabs = [
    { id: "profile", label: t('profile') },
    { id: "security", label: t('security') },
    { id: "appearance", label: t('theme') },
    { id: "language", label: t('language') },
    { id: "feedback", label: t('feedback') },
    { id: "data", label: t('data') }
  ];

  return (
    <div className={`container max-w-6xl py-8 ${isMobile ? 'px-2' : 'px-4'}`}>
      <div className="flex items-center mb-8">
        {isMobile && (
          <Button 
            variant="ghost" 
            onClick={handleGoBack} 
            className="mr-2"
            aria-label={t('back')}
          >
            <ChevronLeft className="h-5 w-5" />
          </Button>
        )}
        <h1 className="text-3xl font-bold text-left">{t('settings')}</h1>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
        <div className="md:col-span-3">
          <Card className="p-2">
            <div className="flex flex-col space-y-1">
              {tabs.map((tab) => (
                <Button
                  key={tab.id}
                  variant={activeTab === tab.id ? "default" : "ghost"}
                  className={`justify-start text-left h-10 px-3 ${activeTab === tab.id ? "" : "hover:bg-accent"}`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  <span className="flex items-center">
                    {getTabIcon(tab.id)}
                    <span className="ml-2">{tab.label}</span>
                  </span>
                </Button>
              ))}
              
              {isMobile && (
                <Button
                  variant="destructive"
                  className="justify-start text-left h-10 px-3 mt-2"
                  onClick={handleSignOut}
                >
                  <span className="flex items-center">
                    <LogOut className="h-5 w-5" />
                    <span className="ml-2">{t('sign_out')}</span>
                  </span>
                </Button>
              )}
            </div>
          </Card>
        </div>

        <div className="md:col-span-9">
          {renderActiveTabContent()}
        </div>
      </div>
    </div>
  );
}
